import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getMemberFlats,
    getMemberInvoices,
    getMemberSummary,
} from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { getNoticeBoard } from '../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_MY_BILLS,
    SOCIETY_PORTAL_MY_BILL_DETAIL,
    SOCIETY_PORTAL_MY_PAYMENTS,
    SOCIETY_PORTAL_MY_LEDGER,
    SOCIETY_PORTAL_MY_COMPLAINTS,
    SOCIETY_PORTAL_NOTICE_BOARD,
    SOCIETY_PORTAL_NOTICES_VIEW,
} from '../../../Routing/routes';
import {
    fmtCurrency,
    fmtDate,
    fmtPeriod,
    flatLabel,
    isOverdue,
    daysOverdue,
} from './memberConstants';

const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
};

const Tile = ({ accent, label, value, meta, emphasis }) => (
    <div className="tw-relative tw-overflow-hidden tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
        <span className={`tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-1 ${accent}`} />
        <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">{label}</p>
        <p className={`tw-text-xl tw-font-bold tw-leading-tight tw-mt-1 ${emphasis || 'tw-text-gray-800'}`}>{value}</p>
        {meta && <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{meta}</p>}
    </div>
);

// The member's landing screen: what they owe, which flat it is about, and the
// handful of things asking for their attention right now. The tiles come from
// /api/member/summary; the attention list needs the actual overdue rows, so it
// is derived from the bills the screen already loads.
const MyHome = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        summaryData,
        flatsData,
        flatsLoading,
        invoicesData,
        invoicesLoading,
    } = useSelector((state) => state.MemberPortalReducer || {});
    const { noticeBoardData } = useSelector((state) => state.NoticeReducer || {});

    // Each My Home page has its own member_portal.* permission. The overview
    // route needs only member_portal.overview, so the bills, notices and
    // shortcut panels here are shown — and their data fetched — only when the
    // caller also holds the page they lead to; otherwise a resident with a
    // trimmed role would see a 403 toast for a panel they cannot open anyway.
    const { can } = usePermissions();
    const canBills = can(PERMISSIONS.MEMBER_PORTAL_BILLS);
    const canPayments = can(PERMISSIONS.MEMBER_PORTAL_PAYMENTS);
    const canLedger = can(PERMISSIONS.MEMBER_PORTAL_LEDGER);
    const canComplaints = can(PERMISSIONS.MEMBER_PORTAL_COMPLAINTS);
    const canNotices = can(PERMISSIONS.MEMBER_PORTAL_NOTICE_BOARD);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (!societyId) return;
        dispatch(getMemberSummary({ societyId }));
        dispatch(getMemberFlats({ societyId }));
        // A member has a handful of bills, not thousands — one page covers the
        // arrears list without paging.
        if (canBills) dispatch(getMemberInvoices({ societyId, page: 1, limit: 50 }));
        if (canNotices) dispatch(getNoticeBoard({ societyId, page: 1, limit: 5 }));
    }, [dispatch, societyId, canBills, canNotices, refreshKey]);

    const flats = useMemo(() => (Array.isArray(flatsData) ? flatsData : []), [flatsData]);
    // Guarded on the permission too, so data left in the store by another
    // screen never surfaces on a panel this caller is not meant to see.
    const bills = useMemo(() => (canBills && Array.isArray(invoicesData) ? invoicesData : []), [canBills, invoicesData]);
    const notices = useMemo(() => (canNotices && Array.isArray(noticeBoardData) ? noticeBoardData : []), [canNotices, noticeBoardData]);

    const overdueBills = useMemo(() => bills.filter(isOverdue), [bills]);
    const oldestOverdue = useMemo(
        () => overdueBills.reduce(
            (acc, b) => (!acc || new Date(b.due_date) < new Date(acc.due_date) ? b : acc),
            null
        ),
        [overdueBills]
    );

    // Prefer the server's figures; fall back to the loaded rows so the screen
    // still says something sensible before /summary resolves.
    const s = summaryData || {};
    const outstanding = s.outstanding_amount ?? bills.reduce((sum, b) => sum + parseFloat(b.balance_due || 0), 0);
    const overdueCount = s.overdue_count ?? overdueBills.length;
    const oldestDueDate = s.oldest_due_date ?? oldestOverdue?.due_date ?? null;
    const oldestDueDays = s.oldest_due_days ?? (oldestOverdue ? daysOverdue(oldestOverdue) : 0);
    const paidThisYear = s.paid_this_year ?? bills.reduce((sum, b) => sum + parseFloat(b.amount_paid || 0), 0);
    const receiptCount = s.receipt_count;
    const openComplaints = s.open_complaints ?? 0;
    const inProgressComplaints = s.in_progress_complaints ?? 0;

    const pendingAck = notices.filter((n) => n.requires_acknowledgement && !n.acknowledged_by_me);
    const urgentNotices = notices.filter((n) => n.priority === 'URGENT' || n.priority === 'HIGH');

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-max-w-5xl tw-mx-auto tw-space-y-4">

                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">{greeting()}</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Your bills, payments and society updates in one place
                        </p>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {canBills && (
                            <button
                                onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_BILLS)}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-shadow-sm"
                            >
                                <Icon icon="hugeicons:invoice-02" /> View my bills
                            </button>
                        )}
                        {canLedger && (
                            <button
                                onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_LEDGER)}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-border-sky-400 hover:tw-text-sky-700"
                            >
                                <Icon icon="hugeicons:notebook-01" /> Statement
                            </button>
                        )}
                    </div>
                </div>

                <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
                    <Tile
                        accent="tw-bg-rose-500"
                        label="Outstanding"
                        value={fmtCurrency(outstanding)}
                        meta={overdueCount > 0 ? `${overdueCount} bill${overdueCount === 1 ? '' : 's'} overdue` : 'Nothing overdue'}
                        emphasis={parseFloat(outstanding) > 0 ? 'tw-text-rose-600' : 'tw-text-emerald-600'}
                    />
                    <Tile
                        accent="tw-bg-amber-500"
                        label="Oldest due since"
                        value={oldestDueDate ? fmtDate(oldestDueDate) : '-'}
                        meta={oldestDueDate ? `${oldestDueDays} days · interest accruing` : 'All bills within due date'}
                    />
                    <Tile
                        accent="tw-bg-emerald-500"
                        label="Paid this year"
                        value={fmtCurrency(paidThisYear)}
                        meta={receiptCount != null ? `${receiptCount} receipt${receiptCount === 1 ? '' : 's'}` : undefined}
                    />
                    <Tile
                        accent="tw-bg-sky-500"
                        label="Open complaints"
                        value={openComplaints}
                        meta={inProgressComplaints > 0 ? `${inProgressComplaints} in progress` : 'Nothing pending'}
                    />
                </div>

                {/* My flat(s) */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">
                            {flats.length > 1 ? 'My flats' : 'My flat'}
                        </h2>
                    </div>
                    {flatsLoading && flats.length === 0 ? (
                        <div className="tw-p-6 tw-text-center tw-text-[11px] tw-text-gray-400">Loading...</div>
                    ) : flats.length === 0 ? (
                        <div className="tw-p-6 tw-text-center tw-text-[11px] tw-text-gray-500">
                            No flat is linked to your account in this society. Please contact the society office.
                        </div>
                    ) : (
                        <div className="tw-divide-y tw-divide-gray-50">
                            {flats.map((f) => (
                                <div key={f.flat_id} className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4">
                                    {[
                                        ['Unit', flatLabel(f)],
                                        ['Floor / type', [f.floor_number, f.flat_type].filter(Boolean).join(' · ') || '-'],
                                        ['Carpet area', f.carpet_area ? `${f.carpet_area} sq.ft` : '-'],
                                        ['Occupancy', f.ownership_type || '-'],
                                    ].map(([k, v]) => (
                                        <div key={k} className="tw-p-4 tw-border-r tw-border-gray-50 last:tw-border-r-0">
                                            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">{k}</p>
                                            <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-mt-0.5">{v}</p>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Needs your attention */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Needs your attention</h2>
                    </div>

                    {overdueBills.length === 0 && pendingAck.length === 0 && urgentNotices.length === 0 ? (
                        <div className="tw-p-8 tw-text-center">
                            <Icon icon="mdi:check-circle-outline" className="tw-text-3xl tw-text-emerald-300 tw-mx-auto" />
                            <p className="tw-text-xs tw-text-gray-500 tw-mt-2">You're all caught up</p>
                        </div>
                    ) : (
                        <div className="tw-divide-y tw-divide-gray-50">
                            {overdueBills.length > 0 && (
                                <div className="tw-p-4 tw-flex tw-items-start tw-gap-3">
                                    <span className="tw-w-1 tw-self-stretch tw-rounded tw-bg-rose-500 tw-flex-shrink-0" />
                                    <div className="tw-flex-1 tw-min-w-0">
                                        <p className="tw-text-sm tw-font-semibold tw-text-gray-800">
                                            {overdueBills.length === 1
                                                ? 'One maintenance bill is overdue'
                                                : `${overdueBills.length} maintenance bills are overdue`}
                                        </p>
                                        <p className="tw-text-[11px] tw-font-mono tw-text-gray-500 tw-mt-0.5">
                                            {overdueBills.map((b) => b.document_number).filter(Boolean).join(', ')}
                                        </p>
                                        <p className="tw-text-[11px] tw-text-gray-600 tw-mt-1">
                                            Interest keeps accruing on the balance until it is cleared.
                                        </p>
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-items-end tw-gap-1.5 tw-flex-shrink-0">
                                        <span className="tw-text-sm tw-font-bold tw-text-rose-600">
                                            {fmtCurrency(overdueBills.reduce((s2, b) => s2 + parseFloat(b.balance_due || 0), 0))}
                                        </span>
                                        <button
                                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_BILLS)}
                                            className="tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700"
                                        >
                                            Review bills
                                        </button>
                                    </div>
                                </div>
                            )}

                            {pendingAck.map((n) => (
                                <div key={`ack-${n.notice_id}`} className="tw-p-4 tw-flex tw-items-start tw-gap-3">
                                    <span className="tw-w-1 tw-self-stretch tw-rounded tw-bg-amber-500 tw-flex-shrink-0" />
                                    <div className="tw-flex-1 tw-min-w-0">
                                        <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate">{n.title}</p>
                                        <p className="tw-text-[11px] tw-text-gray-600 tw-mt-0.5">
                                            The committee has asked every member to confirm they have read this.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() =>
                                            navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_VIEW.replace(':id', n.notice_id))
                                        }
                                        className="tw-flex-shrink-0 tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-text-amber-800 tw-bg-amber-50 hover:tw-bg-amber-100"
                                    >
                                        Acknowledge
                                    </button>
                                </div>
                            ))}

                            {urgentNotices
                                .filter((n) => !pendingAck.some((p) => p.notice_id === n.notice_id))
                                .map((n) => (
                                    <div key={`urgent-${n.notice_id}`} className="tw-p-4 tw-flex tw-items-start tw-gap-3">
                                        <span className="tw-w-1 tw-self-stretch tw-rounded tw-bg-sky-500 tw-flex-shrink-0" />
                                        <div className="tw-flex-1 tw-min-w-0">
                                            <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate">{n.title}</p>
                                            <p className="tw-text-[11px] tw-text-gray-600 tw-mt-0.5 tw-line-clamp-2">{n.summary}</p>
                                        </div>
                                        <button
                                            onClick={() =>
                                                navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_VIEW.replace(':id', n.notice_id))
                                            }
                                            className="tw-flex-shrink-0 tw-text-[11px] tw-font-medium tw-text-sky-700 hover:tw-text-sky-800"
                                        >
                                            Read
                                        </button>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Recent bills */}
                {canBills && (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Recent bills</h2>
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_BILLS)}
                            className="tw-text-[11px] tw-font-medium tw-text-sky-700 hover:tw-text-sky-800"
                        >
                            See all
                        </button>
                    </div>
                    {bills.length === 0 ? (
                        <div className="tw-p-8 tw-text-center tw-text-[11px] tw-text-gray-500">
                            {invoicesLoading ? 'Loading...' : 'No bills have been raised on your flat yet'}
                        </div>
                    ) : (
                        <div className="tw-divide-y tw-divide-gray-50">
                            {bills.slice(0, 5).map((b) => (
                                <button
                                    key={b.invoice_id}
                                    onClick={() =>
                                        navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_BILL_DETAIL.replace(':id', b.invoice_id))
                                    }
                                    className="tw-w-full tw-text-left tw-p-4 hover:tw-bg-slate-50/50 tw-flex tw-items-center tw-gap-3"
                                >
                                    <div className="tw-flex-1 tw-min-w-0">
                                        <p className="tw-text-xs tw-font-mono tw-font-semibold tw-text-gray-700">
                                            {b.document_number || '-'}
                                        </p>
                                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                            {fmtPeriod(b.period_start, b.period_end)} &middot; due {fmtDate(b.due_date)}
                                        </p>
                                    </div>
                                    <div className="tw-text-right tw-flex-shrink-0">
                                        <p className="tw-text-xs tw-font-semibold tw-text-gray-800">{fmtCurrency(b.net_payable)}</p>
                                        <p className={`tw-text-[11px] tw-font-medium ${parseFloat(b.balance_due || 0) > 0 ? 'tw-text-rose-600' : 'tw-text-emerald-600'}`}>
                                            {parseFloat(b.balance_due || 0) > 0 ? `${fmtCurrency(b.balance_due)} due` : 'Paid'}
                                        </p>
                                    </div>
                                    <Icon icon="mdi:chevron-right" className="tw-text-gray-400 tw-text-lg tw-flex-shrink-0" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                )}

                {(canPayments || canComplaints || canNotices) && (
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                    {canPayments && (
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_PAYMENTS)}
                            className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-text-left hover:tw-border-sky-200"
                        >
                            <Icon icon="mdi:receipt-text-check-outline" className="tw-text-xl tw-text-emerald-500" />
                            <p className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-2">My payments</p>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Receipts and what each one settled</p>
                        </button>
                    )}
                    {canComplaints && (
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_COMPLAINTS)}
                            className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-text-left hover:tw-border-sky-200"
                        >
                            <Icon icon="hugeicons:message-notification-01" className="tw-text-xl tw-text-rose-500" />
                            <p className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-2">My complaints</p>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Track what you've raised, or report something new</p>
                        </button>
                    )}
                    {canNotices && (
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICE_BOARD)}
                            className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-text-left hover:tw-border-sky-200"
                        >
                            <Icon icon="mdi:bulletin-board" className="tw-text-xl tw-text-sky-500" />
                            <p className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-2">Notice board</p>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                {notices.length > 0 ? `${notices.length} item${notices.length === 1 ? '' : 's'} on the board` : 'Notices and circulars for your wing'}
                            </p>
                        </button>
                    )}
                </div>
                )}
            </div>
        </div>
    );
};

export default MyHome;
